/**
 * Nexus Investigators — Activity Log tab
 *
 * Admin surface that lists every tracked user movement in NexusTrac:
 *   • Logins, logouts, password resets
 *   • Case activity (create, update, evidence, subjects, vehicles, notes)
 *   • Skip-trace searches and OSINT lookups
 *   • Field Ops / Time Clock events
 *   • GPS duty-status changes
 *   • Report exports
 *
 * Filters: user, category, action, date range, free-text search.
 * Shows IP, user agent (device), duration, HTTP status and the resource
 * involved, so admins can audit any single investigator's trail.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BACKEND_URL } from '../config/canonical';
import {
  Activity, RefreshCw, Filter, User as UserIcon, Clock, MapPin,
  Shield, ArrowLeft, Search,
} from 'lucide-react';

const CAT_COLORS = {
  AUTH:          'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  CASE:          'bg-blue-500/20 text-blue-300 border-blue-500/40',
  CASE_SUBJECT:  'bg-blue-500/20 text-blue-300 border-blue-500/40',
  CASE_VEHICLE:  'bg-blue-500/20 text-blue-300 border-blue-500/40',
  CASE_LEAD:     'bg-blue-500/20 text-blue-300 border-blue-500/40',
  CASE_INTERVIEW:'bg-blue-500/20 text-blue-300 border-blue-500/40',
  CASE_NOTE:     'bg-blue-500/20 text-blue-300 border-blue-500/40',
  CASE_LOCATION: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  EVIDENCE:      'bg-purple-500/20 text-purple-300 border-purple-500/40',
  SKIP_TRACE:    'bg-pink-500/20 text-pink-300 border-pink-500/40',
  FIELD_OPS:     'bg-amber-500/20 text-amber-300 border-amber-500/40',
  TIME_CLOCK:    'bg-amber-500/20 text-amber-300 border-amber-500/40',
  GPS:           'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  REPORT:        'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
  AI:            'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40',
  USER:          'bg-slate-500/20 text-slate-300 border-slate-500/40',
  CLIENT:        'bg-teal-500/20 text-teal-300 border-teal-500/40',
  APPLICANT:     'bg-lime-500/20 text-lime-300 border-lime-500/40',
  WARRANT:       'bg-red-500/20 text-red-300 border-red-500/40',
  ATA_WARRANT:   'bg-red-500/20 text-red-300 border-red-500/40',
};

const prettyDT = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const prettyAction = (a) => (a || '').replace(/_/g, ' ');

export default function NexusInvestigatorActivity() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const H = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    user_id: '', category: '', action: '', date_from: '', date_to: '', q: '',
  });
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const loadLookups = useCallback(async () => {
    const [u, c, s] = await Promise.all([
      fetch(`${BACKEND_URL}/api/users`, { headers: H }).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${BACKEND_URL}/api/activity-logs/categories`, { headers: H }).then(r => r.ok ? r.json() : { categories: [] }).catch(() => ({ categories: [] })),
      fetch(`${BACKEND_URL}/api/activity-logs/stats?days=7`, { headers: H }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    setUsers(Array.isArray(u) ? u : (u?.users || []));
    setCategories(c.categories || []);
    setStats(s);
  }, [H]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('limit', '300');
      const r = await fetch(`${BACKEND_URL}/api/activity-logs?${params.toString()}`, { headers: H });
      const d = r.ok ? await r.json() : { items: [] };
      setItems(d.items || []);
    } finally { setLoading(false); }
  }, [filters, H]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);

  const applySearch = () => setFilters(f => ({ ...f, q: search.trim() }));
  const clear = () => {
    setFilters({ user_id: '', category: '', action: '', date_from: '', date_to: '', q: '' });
    setSearch('');
  };

  return (
    <div className="space-y-5" data-testid="nexus-investigator-activity-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/team')} className="border-slate-600 !text-slate-200" data-testid="back-to-team">
            <ArrowLeft className="w-4 h-4 mr-1" /> Investigators
          </Button>
          <div className="w-12 h-12 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
            <Activity className="w-6 h-6 text-cyan-300" />
          </div>
          <div>
            <div className="text-xs text-cyan-300 uppercase tracking-wider font-mono">Nexus Investigators · Audit Trail</div>
            <h1 className="text-2xl font-bold text-white">NexusTrac Activity Log</h1>
            <p className="text-slate-400 text-sm">Every tracked movement — logins, case actions, skip-traces, reports, GPS, and more.</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => { load(); loadLookups(); }} className="border-slate-600 !text-slate-200" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-[#131A2A] border border-[#334155] rounded-lg px-3 py-2">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Total · Last {stats.window_days} days</div>
            <div className="text-2xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="bg-[#131A2A] border border-[#334155] rounded-lg px-3 py-2">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Unique Users</div>
            <div className="text-2xl font-bold text-white">{stats.by_user.length}</div>
          </div>
          <div className="bg-[#131A2A] border border-[#334155] rounded-lg px-3 py-2 col-span-2">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Top Categories</div>
            <div className="flex gap-1 flex-wrap">
              {stats.by_category.slice(0, 6).map(c => (
                <Badge key={c.category} className={`text-[10px] border ${CAT_COLORS[c.category] || 'bg-slate-700 text-slate-200'}`}>
                  {prettyAction(c.category)} · {c.count}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Investigator</label>
            <Select value={filters.user_id || 'all'} onValueChange={v => setFilters(f => ({ ...f, user_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="filter-user"><SelectValue placeholder="Everyone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.name || u.username}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Category</label>
            <Select value={filters.category || 'any'} onValueChange={v => setFilters(f => ({ ...f, category: v === 'any' ? '' : v }))}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="filter-category"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {categories.map(c => <SelectItem key={c.category} value={c.category}>{prettyAction(c.category)} ({c.count})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Action</label>
            <Input placeholder="e.g. LOGIN_SUCCESS" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value.toUpperCase() }))}
              className="bg-slate-800 border-slate-600 text-white" data-testid="filter-action" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">From</label>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
              className="bg-slate-800 border-slate-600 text-white" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">To</label>
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
              className="bg-slate-800 border-slate-600 text-white" data-testid="filter-date-to" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Search</label>
            <div className="flex gap-1">
              <Input placeholder="path / action / resource" value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
                className="bg-slate-800 border-slate-600 text-white" data-testid="filter-search" />
              <Button onClick={applySearch} variant="outline" className="border-slate-600 !text-slate-200 px-2">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="md:col-span-6 flex justify-end">
            <Button variant="ghost" onClick={clear} className="text-slate-400 text-xs" data-testid="clear-filters">Clear all</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardContent className="p-0 overflow-x-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">
              {loading ? 'Loading…' : 'No activity matches these filters.'}
            </div>
          ) : (
            <table className="w-full text-sm text-slate-200">
              <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="p-2 text-left">When</th>
                  <th className="p-2 text-left">Who</th>
                  <th className="p-2 text-left">Category</th>
                  <th className="p-2 text-left">Action</th>
                  <th className="p-2 text-left">Resource</th>
                  <th className="p-2 text-left">IP · Device</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map(a => {
                  const cat = (a.category || 'OTHER').toUpperCase();
                  const ua = (a.user_agent || '').slice(0, 50);
                  return (
                    <tr key={a.id} className="border-t border-slate-800 hover:bg-slate-900/30" data-testid={`row-${a.id}`}>
                      <td className="p-2 text-xs whitespace-nowrap text-slate-300">
                        <Clock className="w-3 h-3 inline mr-1 opacity-60" />{prettyDT(a.timestamp)}
                      </td>
                      <td className="p-2 text-xs">
                        <button
                          type="button"
                          onClick={() => a.user_id && setFilters(f => ({ ...f, user_id: a.user_id }))}
                          className="flex items-center gap-1 hover:text-cyan-300 transition-colors text-left"
                          title={a.user_id ? 'Filter log by this investigator' : ''}
                          data-testid={`row-user-${a.id}`}
                        >
                          <UserIcon className="w-3 h-3 text-slate-400" />
                          <span className="underline-offset-2 hover:underline">{a.user_name || a.user_id || '—'}</span>
                        </button>
                        {a.role && <div className="text-[10px] text-slate-500">{prettyAction(a.role)}</div>}
                      </td>
                      <td className="p-2">
                        <Badge className={`text-[10px] border ${CAT_COLORS[cat] || 'bg-slate-700 text-slate-200'}`}>{prettyAction(cat)}</Badge>
                      </td>
                      <td className="p-2 text-xs font-mono text-white">{prettyAction(a.action)}</td>
                      <td className="p-2 text-xs text-slate-400">
                        {a.resource_type ? <>
                          <Shield className="w-3 h-3 inline mr-1 opacity-60" />
                          {a.resource_type}
                          {a.resource_id ? <> · <span className="text-slate-300">{a.resource_id}</span></> : null}
                        </> : <span className="opacity-40">—</span>}
                        {a.details?.path && <div className="text-[10px] text-slate-600 font-mono">{a.details.method} {a.details.path}</div>}
                      </td>
                      <td className="p-2 text-xs text-slate-400">
                        {a.ip_address ? <><MapPin className="w-3 h-3 inline mr-1 opacity-60" />{a.ip_address}</> : <span className="opacity-40">—</span>}
                        {ua && <div className="text-[10px] text-slate-600" title={a.user_agent}>{ua}{a.user_agent?.length > 50 ? '…' : ''}</div>}
                        {a.location && (a.location.lat != null ? (
                          <div className="text-[10px] text-emerald-400 mt-0.5" title={`GPS · ${a.location.source || 'browser_gps'}`}>
                            GPS {Number(a.location.lat).toFixed(4)},{Number(a.location.lng).toFixed(4)}
                            {a.location.accuracy_m ? <span className="text-slate-500"> ±{Math.round(a.location.accuracy_m)}m</span> : null}
                          </div>
                        ) : a.location.status ? (
                          <div className="text-[10px] text-amber-400 mt-0.5">GPS {a.location.status}</div>
                        ) : null)}
                      </td>
                      <td className="p-2 text-xs">
                        {a.status_code != null && (
                          <Badge className={`text-[10px] ${a.status_code < 300 ? 'bg-emerald-500/20 text-emerald-300' : a.status_code < 400 ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'}`}>
                            {a.status_code}
                          </Badge>
                        )}
                        {a.duration_ms != null && <div className="text-[10px] text-slate-500 mt-0.5">{a.duration_ms}ms</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
